import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Plus, Edit, Trash2, EyeOff, Eye, Layers } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "../../components/admin/ImageUploader";

const EMPTY = { name: "", slug: "", image: "", sort_order: 100, is_active: true };

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/categories").then((r) => setCats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      if (editing) await api.put(`/admin/categories/${editing}`, form);
      else await api.post("/admin/categories", form);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const toggle = async (c) => {
    await api.put(`/admin/categories/${c.category_id}`, { ...c, is_active: !c.is_active });
    toast.success(c.is_active ? "Hidden from storefront" : "Shown on storefront");
    load();
  };

  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try {
      await api.delete(`/admin/categories/${c.category_id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const edit = (c) => {
    setEditing(c.category_id);
    setForm({
      name: c.name, slug: c.slug, image: c.image || "",
      sort_order: c.sort_order || 100, is_active: c.is_active !== false,
    });
    setOpen(true);
  };

  const visible = cats.filter((c) => c.is_active !== false).length;
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-outfit font-bold flex items-center gap-2">
            <Layers className="w-6 h-6 text-[#D4AF37]" /> Categories
          </h1>
          <div className="text-sm text-stone-500">
            {visible} visible / {cats.length} total. Hidden categories don't appear as filters and their products stay off the storefront.
          </div>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
          <DialogTrigger asChild>
            <button data-testid="new-cat-btn" className="bg-[#3B2F2F] text-white rounded-md px-4 py-2 flex items-center gap-2">
              <Plus className="w-4 h-4" /> New category
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit" : "New"} category</DialogTitle>
              <DialogDescription>Hidden categories are removed from storefront filters and their products won't appear in listings.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Name" value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="cat-name" />
              <Input placeholder="Slug (optional, auto-generated)" value={form.slug} onChange={(e) => upd("slug", e.target.value)} />
              <Input placeholder="Image URL" value={form.image} onChange={(e) => upd("image", e.target.value)} />
              <div>
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Or upload</div>
                <ImageUploader value={form.image ? [form.image] : []} onChange={(urls) => upd("image", urls[0] || "")} kind="categories" />
              </div>
              <Input type="number" placeholder="Sort order (lower = first)" value={form.sort_order} onChange={(e) => upd("sort_order", Number(e.target.value))} />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_active} onChange={(e) => upd("is_active", e.target.checked)} data-testid="cat-active" />
                Show on storefront
              </label>
              <button onClick={save} data-testid="save-cat-btn" className="w-full bg-[#3B2F2F] text-white rounded-md py-2">{editing ? "Update" : "Create"}</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Category</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Products</TableHead>
              <TableHead>Sort</TableHead>
              <TableHead>Visibility</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cats.map((c) => (
              <TableRow key={c.category_id} data-testid={`cat-row-${c.slug}`}>
                <TableCell className="flex items-center gap-3">
                  {c.image && <img src={c.image} alt="" className="w-10 h-10 rounded object-cover" />}
                  <span className="font-medium">{c.name}</span>
                </TableCell>
                <TableCell className="text-xs text-stone-500">{c.slug}</TableCell>
                <TableCell><span className="font-outfit font-semibold">{c.product_count || 0}</span></TableCell>
                <TableCell>{c.sort_order || 100}</TableCell>
                <TableCell>
                  <button
                    onClick={() => toggle(c)}
                    data-testid={`cat-toggle-${c.slug}`}
                    className={`text-xs rounded-full px-3 py-1 flex items-center gap-1 ${c.is_active !== false ? "bg-[#E8EDDF] text-[#2E4A35]" : "bg-stone-100 text-stone-500"}`}>
                    {c.is_active !== false ? <><Eye className="w-3 h-3" /> Visible</> : <><EyeOff className="w-3 h-3" /> Hidden</>}
                  </button>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <button onClick={() => edit(c)} className="text-stone-500 hover:text-[#3B2F2F]"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => del(c)} className="text-stone-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
